import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Users, Activity, HardDrive, Settings, AlertTriangle } from 'lucide-react';
import { apiClient } from '../api/client';
import { formatFileSize } from '../utils/formatters';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  adminUsers: number;
  totalStorage: number;
  systemHealth: 'Healthy' | 'Warning' | 'Critical';
  activeAlerts: number;
}

export const AdminPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminStats = async () => {
      try {
        setLoading(true);
        
        // Fetch user stats
        const userStatsResponse = await apiClient.get('/users/stats/overview');
        
        // Fetch file stats for storage info
        const fileStatsResponse = await apiClient.get('/files/stats/overview');
        
        if (userStatsResponse.data.success && fileStatsResponse.data.success) {
          const userData = userStatsResponse.data.data;
          const fileData = fileStatsResponse.data.data;
          
          setStats({
            totalUsers: userData.totalUsers,
            activeUsers: userData.activeUsers,
            adminUsers: userData.adminUsers,
            totalStorage: fileData.totalSize,
            systemHealth: 'Healthy', // Could be calculated based on various metrics
            activeAlerts: 0, // Could be based on error logs, storage limits, etc.
          });
        }
      } catch (error) {
        console.error('Failed to fetch admin stats:', error);
        // Set fallback values
        setStats({
          totalUsers: 0,
          activeUsers: 0,
          adminUsers: 0,
          totalStorage: 0,
          systemHealth: 'Warning',
          activeAlerts: 1,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAdminStats();
  }, []);

  if (!user?.isAdmin) {
    return null; // This should be handled by ProtectedRoute, but just in case
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System Administration</h1>
        <p className="text-gray-600 mt-1">
          Manage users, monitor system health, and configure settings
        </p>
      </div>

      {/* Admin Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '-' : stats?.totalUsers || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-brand-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">System Health</p>
                <p className={`text-2xl font-bold ${
                  loading ? 'text-gray-400' :
                  stats?.systemHealth === 'Healthy' ? 'text-success-600' :
                  stats?.systemHealth === 'Warning' ? 'text-warning-600' : 'text-error-600'
                }`}>
                  {loading ? 'Loading...' : stats?.systemHealth || 'Unknown'}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                loading ? 'bg-gray-100' :
                stats?.systemHealth === 'Healthy' ? 'bg-success-100' :
                stats?.systemHealth === 'Warning' ? 'bg-warning-100' : 'bg-error-100'
              }`}>
                <Activity className={`w-6 h-6 ${
                  loading ? 'text-gray-400' :
                  stats?.systemHealth === 'Healthy' ? 'text-success-600' :
                  stats?.systemHealth === 'Warning' ? 'text-warning-600' : 'text-error-600'
                }`} />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Storage Used</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '-' : formatFileSize(stats?.totalStorage || 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Alerts</p>
                <p className={`text-2xl font-bold ${
                  loading ? 'text-gray-400' :
                  (stats?.activeAlerts || 0) === 0 ? 'text-success-600' : 'text-warning-600'
                }`}>
                  {loading ? '-' : stats?.activeAlerts || 0}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                loading ? 'bg-gray-100' :
                (stats?.activeAlerts || 0) === 0 ? 'bg-success-100' : 'bg-warning-100'
              }`}>
                <AlertTriangle className={`w-6 h-6 ${
                  loading ? 'text-gray-400' :
                  (stats?.activeAlerts || 0) === 0 ? 'text-success-600' : 'text-warning-600'
                }`} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Management Section */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900">Admin Users</p>
                <p className="text-xs text-gray-600">Full system access</p>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {loading ? '-' : stats?.adminUsers || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900">Regular Users</p>
                <p className="text-xs text-gray-600">Upload and view files</p>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {loading ? '-' : (stats?.totalUsers || 0) - (stats?.adminUsers || 0)}
              </span>
            </div>
          </div>
          <button 
            onClick={() => navigate('/admin/users')}
            className="w-full btn-primary"
          >
            <Users className="w-5 h-5 mr-2" />
            Manage Users
          </button>
        </div>
      </div>

      {/* Placeholder for future admin features */}
      <div className="card">
        <div className="card-body text-center py-12">
          <Settings className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Additional Admin Features Coming Soon
          </h3>
          <p className="text-gray-600 max-w-sm mx-auto">
            User management, detailed analytics, system monitoring, and audit log viewing will be available in future releases.
          </p>
        </div>
      </div>
    </div>
  );
};